import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Hairline,
  Numeric,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP, formatTimestamp } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import { MarkPaidForm } from "../components/MarkPaidForm.js";
import { DemoteToDraftButton } from "../components/DemoteToDraftButton.js";
import type { InvoiceDTO, InvoiceStatus } from "@wmart/shared-types";

const STATUS_TONE: Record<InvoiceStatus, Tone> = {
  draft: "neutral",
  issued: "info",
  sent: "info",
  paid: "success",
  overdue: "danger",
  void: "neutral",
};

/**
 * Admin invoice detail (P15-T10).
 *
 * Sections:
 *  - Header: number, status, totals, linked order
 *  - Items table (snapshot at issuance)
 *  - VAT breakdown (grouped per category)
 *  - Action sidebar:
 *      - Mark paid (modal form) — when status ∈ draft/issued/sent/overdue
 *      - Demote to draft        — when status === issued + no payments
 *      - View PDF (buyer link)  — when pdfObjectKey is present
 *
 * Payments history is intentionally NOT surfaced on V1.5 — the API does not
 * yet expose `GET /v1/admin/invoices/:id` with the `payments` array (only
 * the top-level invoice fields). Carry-forward.
 */
export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { notify, notifyProblem } = useToast();
  const [invoice, setInvoice] = useState<InvoiceDTO | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);

  const refetch = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<InvoiceDTO>(`/v1/admin/invoices/${id}`);
      setInvoice(data);
      setLoadError(false);
    } catch (err) {
      setLoadError(true);
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load invoice");
    }
  }, [id, notify, notifyProblem]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  if (loadError && !invoice) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/invoices"
          className="text-body-sm text-accent hover:underline"
        >
          ← Back to invoices
        </Link>
        <p className="text-body-sm text-danger">Invoice could not be loaded.</p>
      </div>
    );
  }

  if (!invoice) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  // Action visibility — mirrors the API guard set:
  //  - markPaid is gated by status NOT IN ('paid','void')
  //  - demoteToDraft is gated by status === 'issued' (and the service also
  //    checks no payments + no reminder; surface a 422 toast if blocked).
  const canMarkPaid =
    invoice.status === "draft" ||
    invoice.status === "issued" ||
    invoice.status === "sent" ||
    invoice.status === "overdue";
  const canDemote = invoice.status === "issued";

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/invoices"
        className="text-body-sm text-accent hover:underline"
      >
        ← Back to invoices
      </Link>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <section className="flex min-w-0 flex-col gap-6">
          <Card className="p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-serif text-display-sm text-ink-900">
                  Invoice{" "}
                  <span className="font-mono">{invoice.number}</span>
                </h2>
                <p className="mt-1 text-body-sm text-ink-500">
                  {invoice.orderId ? (
                    <>
                      <Link
                        to={`/orders/${invoice.orderId}`}
                        className="text-ink-700 hover:underline"
                      >
                        Linked order {invoice.orderId.slice(0, 8)}…
                      </Link>
                      {" · "}
                    </>
                  ) : (
                    <>Standalone (no linked order) · </>
                  )}
                  issued {formatTimestamp(invoice.issuedAt)}
                  {invoice.dueDate ? <> · due {invoice.dueDate}</> : null}
                  {invoice.paidAt ? (
                    <> · paid {formatTimestamp(invoice.paidAt)}</>
                  ) : null}
                </p>
              </div>
              <StatusPill tone={STATUS_TONE[invoice.status]}>
                {invoice.status}
              </StatusPill>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-serif text-title-md text-ink-900">Items</h3>
            {invoice.items.length === 0 ? (
              <p className="text-body-sm text-ink-500">No items.</p>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Description</TH>
                    <TH className="text-right">Qty</TH>
                    <TH className="text-right">Unit</TH>
                    <TH className="text-right">VAT</TH>
                    <TH className="text-right">Line</TH>
                  </TR>
                </THead>
                <TBody>
                  {invoice.items.map((it) => (
                    <TR key={it.id}>
                      <TD>{it.description}</TD>
                      <TD className="text-right">
                        <Numeric>{it.quantity}</Numeric>
                      </TD>
                      <TD className="text-right">
                        <Numeric serif>
                          {formatGBP(it.unitPriceMinor)}
                        </Numeric>
                      </TD>
                      <TD className="text-right">
                        <Numeric>{formatGBP(it.vatAmountMinor)}</Numeric>
                      </TD>
                      <TD className="text-right">
                        <Numeric serif>
                          {formatGBP(it.lineTotalMinor)}
                        </Numeric>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>

          {invoice.vatLines.length > 0 ? (
            <Card className="p-6">
              <h3 className="font-serif text-title-md text-ink-900">
                VAT breakdown
              </h3>
              <Table>
                <THead>
                  <TR>
                    <TH>Category</TH>
                    <TH className="text-right">Rate</TH>
                    <TH className="text-right">Taxable</TH>
                    <TH className="text-right">VAT</TH>
                  </TR>
                </THead>
                <TBody>
                  {invoice.vatLines.map((v) => (
                    <TR key={v.vatCategoryCode}>
                      <TD className="font-mono text-body-sm">
                        {v.vatCategoryCode}
                      </TD>
                      <TD className="text-right">
                        {(v.rateBasisPoints / 100).toFixed(2)}%
                      </TD>
                      <TD className="text-right">
                        <Numeric>{formatGBP(v.taxableMinor)}</Numeric>
                      </TD>
                      <TD className="text-right">
                        <Numeric>{formatGBP(v.vatMinor)}</Numeric>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          ) : null}
        </section>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card className="flex flex-col gap-3 p-6">
            <h3 className="font-serif text-title-md text-ink-900">Actions</h3>
            {canMarkPaid ? (
              <Button onClick={() => setShowMarkPaid(true)}>
                Mark paid
              </Button>
            ) : null}
            {canDemote ? (
              <DemoteToDraftButton
                invoiceId={invoice.id}
                onSuccess={refetch}
              />
            ) : null}
            {!canMarkPaid && !canDemote ? (
              <p className="text-body-sm text-ink-500">
                No actions available in this state.
              </p>
            ) : null}

            <Hairline spacing="sm" />

            <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
              Totals
            </p>
            <dl className="flex flex-col gap-1 text-body-sm">
              <div className="flex justify-between">
                <dt>Subtotal</dt>
                <dd>
                  <Numeric>{formatGBP(invoice.subtotalMinor)}</Numeric>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>VAT</dt>
                <dd>
                  <Numeric>{formatGBP(invoice.vatMinor)}</Numeric>
                </dd>
              </div>
              <div className="flex justify-between font-medium text-ink-900">
                <dt>Total</dt>
                <dd>
                  <Numeric serif>{formatGBP(invoice.totalMinor)}</Numeric>
                </dd>
              </div>
            </dl>
          </Card>
        </aside>
      </div>

      {showMarkPaid ? (
        <MarkPaidForm
          invoice={invoice}
          onSuccess={refetch}
          onClose={() => setShowMarkPaid(false)}
        />
      ) : null}
    </div>
  );
}
